import { useState, useEffect, useRef } from 'react';
import { Sparkles, Send, FileText, Route, Phone, ClipboardList, MessageSquare, Loader2, Copy, Check, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const AI_FEATURES = [
  {
    id: 'chat',
    title: 'Assistant IA',
    description: 'Posez vos questions sur le BTP, CEE, gestion de projet...',
    icon: MessageSquare,
    color: 'bg-blue-500'
  },
  {
    id: 'relance',
    title: 'Relance Devis',
    description: 'Générez un email de relance professionnel',
    icon: FileText,
    color: 'bg-amber-500'
  },
  {
    id: 'feuille',
    title: 'Feuille de Route',
    description: 'Créez un planning détaillé pour un chantier',
    icon: Route,
    color: 'bg-green-500'
  },
  {
    id: 'script',
    title: 'Script Commercial',
    description: 'Générez un script d\'appel pour vos leads',
    icon: Phone,
    color: 'bg-purple-500'
  },
  {
    id: 'resume',
    title: 'Résumé Chantier',
    description: 'Créez un résumé professionnel pour le client',
    icon: ClipboardList,
    color: 'bg-teal-500'
  }
];

export default function AIModule() {
  const [selectedFeature, setSelectedFeature] = useState('chat');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const chatEndRef = useRef(null);
  
  // Data for selects
  const [devis, setDevis] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');

  useEffect(() => {
    document.title = 'Assistant IA - Reno Resolve';
    fetchData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const fetchData = async () => {
    try {
      const [devisRes, chantiersRes, contactsRes] = await Promise.all([
        api.get('/devis'),
        api.get('/chantiers'),
        api.get('/contacts')
      ]);
      setDevis(devisRes.data);
      setChantiers(chantiersRes.data);
      setContacts(contactsRes.data.filter(c => c.type === 'Lead'));
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const handleGenerate = async () => {
    if (!selectedItem && selectedFeature !== 'chat') {
      toast.error('Veuillez sélectionner un élément');
      return;
    }

    setLoading(true);
    setResult('');

    try {
      let response;
      switch (selectedFeature) {
        case 'relance':
          response = await api.post(`/ai/relance-devis/${selectedItem}`);
          break;
        case 'feuille':
          response = await api.post(`/ai/feuille-route/${selectedItem}`);
          break;
        case 'script':
          response = await api.post(`/ai/script-commercial/${selectedItem}`);
          break;
        case 'resume':
          response = await api.post(`/ai/resume-chantier/${selectedItem}`);
          break;
        default:
          return;
      }
      setResult(response.data.content);
      toast.success('Contenu généré avec succès !');
    } catch (error) {
      console.error('Erreur génération:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    const userMsg = chatMessage;
    setChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatMessage('');
    setLoading(true);

    try {
      const response = await api.post('/ai/chat', { message: userMsg, session_id: 'main' });
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.content }]);
    } catch (error) {
      console.error('Erreur chat:', error);
      setChatHistory(prev => [...prev, { role: 'assistant', content: 'Désolé, une erreur est survenue. Réessayez.' }]);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    toast.success('Copié dans le presse-papier');
    setTimeout(() => setCopied(false), 2000);
  };

  const getSelectOptions = () => {
    switch (selectedFeature) {
      case 'relance':
        return devis.filter(d => ['Envoyé', 'Relancé'].includes(d.statut)).map(d => ({
          value: d.devis_id,
          label: `${d.devis_id} - ${d.montant_estime}€`
        }));
      case 'feuille':
      case 'resume':
        return chantiers.map(c => ({
          value: c.chantier_id,
          label: `${c.chantier_id} - ${c.ville}`
        }));
      case 'script':
        return contacts.map(c => ({
          value: c.contact_id,
          label: `${c.nom_prenom} - ${c.ville || 'N/A'}`
        }));
      default:
        return [];
    }
  };

  const currentFeature = AI_FEATURES.find(f => f.id === selectedFeature);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Sparkles className="w-7 h-7 md:w-8 md:h-8 text-blue-600" />
          Assistant IA
        </h1>
        <p className="text-sm md:text-base text-slate-600 mt-1">
          Générez du contenu professionnel en quelques secondes
        </p>
      </div>

      {/* Feature Selection */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3">
        {AI_FEATURES.map((feature) => (
          <button
            key={feature.id}
            onClick={() => {
              setSelectedFeature(feature.id);
              setSelectedItem('');
              setResult('');
            }}
            className={`p-3 md:p-4 rounded-lg border-2 transition-all text-left ${
              selectedFeature === feature.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className={`w-8 h-8 md:w-10 md:h-10 ${feature.color} rounded-lg flex items-center justify-center mb-2`}>
              <feature.icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm md:text-base">{feature.title}</h3>
            <p className="text-xs text-slate-500 mt-1 hidden md:block">{feature.description}</p>
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {selectedFeature === 'chat' ? (
          /* Chat Interface */
          <div className="flex flex-col h-[500px]">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-700 mb-2">Comment puis-je vous aider ?</h3>
                  <p className="text-sm text-slate-500 max-w-md mx-auto">
                    Posez-moi vos questions sur le BTP, les dispositifs CEE, la gestion de chantier, 
                    les relations clients, ou demandez-moi des conseils.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {[
                      "Comment relancer un devis ?",
                      "Qu'est-ce que le CEE ?",
                      "Conseils négociation client"
                    ].map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setChatMessage(suggestion)}
                        className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                chatHistory.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] md:max-w-[70%] p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-800'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <form onSubmit={handleChat} className="border-t p-4 flex gap-2">
              <Input
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Tapez votre message..."
                disabled={loading}
                className="flex-1"
              />
              <Button type="submit" disabled={loading || !chatMessage.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        ) : (
          /* Generation Interface */
          <div className="p-4 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 ${currentFeature?.color} rounded-lg flex items-center justify-center`}>
                {currentFeature && <currentFeature.icon className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">{currentFeature?.title}</h2>
                <p className="text-sm text-slate-500">{currentFeature?.description}</p>
              </div>
            </div>

            {/* Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {selectedFeature === 'relance' && 'Sélectionnez un devis'}
                {selectedFeature === 'feuille' && 'Sélectionnez un chantier'}
                {selectedFeature === 'script' && 'Sélectionnez un lead'}
                {selectedFeature === 'resume' && 'Sélectionnez un chantier'}
              </label>
              <Select value={selectedItem} onValueChange={setSelectedItem}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent>
                  {getSelectOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading || !selectedItem}
              className="w-full mb-4"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer
                </>
              )}
            </Button>

            {/* Result */}
            {result && (
              <div className="border rounded-lg bg-slate-50">
                <div className="flex items-center justify-between p-3 border-b bg-white rounded-t-lg">
                  <span className="text-sm font-medium text-slate-700">Résultat</span>
                  <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="p-4 max-h-[400px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                    {result}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
